/** Contabilidad → Diario: asientos del emisor en PGC, con alta manual, contabilizar, descartar y anular. */
import { Fragment, useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
} from 'lucide-react'
import { api } from '../../lib/api'
import { formatCents } from '../../lib/money'
import { Link } from 'react-router-dom'
import { entryOrigin, usePgcIssuer, useCanSeeLedger } from '../../lib/ctb'
import CtbHeader from '../../components/ctb/CtbHeader'
import EntryModal, { apiMessage } from '../../components/ctb/EntryModal'
import { Note, selectCls, td, tdR, th, thR } from '../../components/accounting/common'
import {
  CTB_ENTRY_KIND_LABEL,
  CTB_ENTRY_STATUS_LABEL,
  type CtbAccountDto,
  type CtbEntryDto,
} from '@toga/shared'

const STATUS_CLS: Record<string, string> = {
  DRAFT: 'text-amber-300 border-amber-500/40',
  POSTED: 'text-emerald-300 border-emerald-500/40',
  VOID: 't-faint border-base',
}

export default function JournalPage() {
  const canSee = useCanSeeLedger()
  const { pgc, issuerId, setIssuerId, loaded } = usePgcIssuer()
  const [year, setYear] = useState(new Date().getFullYear())
  const [status, setStatus] = useState('')
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [items, setItems] = useState<CtbEntryDto[] | null>(null)
  const [truncated, setTruncated] = useState(false)
  const [accounts, setAccounts] = useState<CtbAccountDto[]>([])
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<Set<string>>(new Set())
  const [modal, setModal] = useState<{ entry: CtbEntryDto | null } | null>(null)
  const [reversing, setReversing] = useState<CtbEntryDto | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250)
    return () => clearTimeout(t)
  }, [q])

  const load = useCallback(async () => {
    if (!issuerId) return
    setError(null)
    try {
      const p = new URLSearchParams({ issuerId, year: String(year) })
      if (status) p.set('status', status)
      if (debouncedQ) p.set('q', debouncedQ)
      const r = await api.get<{ items: CtbEntryDto[]; truncated: boolean }>(`/ctb/entries?${p}`)
      setItems(r.items)
      setTruncated(r.truncated)
    } catch (err) {
      setError(apiMessage(err, 'No se pudo cargar el diario'))
    }
  }, [issuerId, year, status, debouncedQ])

  useEffect(() => {
    void load()
  }, [load, tick])

  useEffect(() => {
    if (!issuerId) return
    api
      .get<{ items: CtbAccountDto[] }>(`/ctb/accounts?issuerId=${issuerId}`)
      .then((r) => setAccounts(r.items))
      .catch(() => setAccounts([]))
  }, [issuerId])

  const reload = () => setTick((t) => t + 1)
  const toggle = (id: string) =>
    setOpen((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      reload()
    } catch (err) {
      setError(apiMessage(err, 'No se pudo completar la operación'))
    } finally {
      setBusy(false)
    }
  }

  if (!canSee) {
    return <p className="t-muted text-sm">El diario es solo para administradores y abogados.</p>
  }

  return (
    <div className="space-y-5 animate-slideUp">
      <CtbHeader
        title="Contabilidad · Diario"
        description="Asientos por partida doble del emisor. Un contabilizado es inmutable: se corrige con un contra-asiento."
        pgc={pgc}
        loaded={loaded}
        issuerId={issuerId}
        onIssuer={setIssuerId}
        year={year}
        onYear={setYear}
      >
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className={selectCls}
          aria-label="Estado"
        >
          <option value="">Todos los estados</option>
          {Object.entries(CTB_ENTRY_STATUS_LABEL).map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por concepto…"
          className={`${selectCls} w-52`}
          aria-label="Buscar"
        />
        <button
          type="button"
          onClick={() => setModal({ entry: null })}
          className="ml-auto px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-toga-600 to-toga-700 hover:shadow-glow-toga inline-flex items-center gap-1.5"
        >
          <Plus size={13} /> Nuevo asiento
        </button>
      </CtbHeader>

      {error && (
        <div className="text-xs rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={13} /> {error}
        </div>
      )}

      {issuerId && (
        <section className="surface overflow-hidden">
          {!items ? (
            <div className="px-5 py-8 t-muted text-sm flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Cargando…
            </div>
          ) : items.length === 0 ? (
            <div className="px-5 py-10 text-center t-muted text-sm">
              No hay asientos en {year}. Crea el primero con «Nuevo asiento».
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-base">
                    <th className={th} />
                    <th className={th}>N.º</th>
                    <th className={th}>Fecha</th>
                    <th className={th}>Concepto</th>
                    <th className={th}>Estado</th>
                    <th className={thR}>Importe</th>
                    <th className={th} />
                  </tr>
                </thead>
                <tbody>
                  {items.map((e) => {
                    const expanded = open.has(e.id)
                    return (
                      <Fragment key={e.id}>
                        <tr className="border-b border-base/50 hover:bg-hover/40">
                          <td className={`${td} w-6`}>
                            <button
                              type="button"
                              onClick={() => toggle(e.id)}
                              className="t-muted"
                              aria-label="Ver líneas"
                            >
                              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>
                          </td>
                          <td className={`${td} font-mono`}>{e.number ?? '—'}</td>
                          <td className={`${td} whitespace-nowrap`}>
                            {new Date(e.date).toLocaleDateString('es-ES', { timeZone: 'UTC' })}
                          </td>
                          <td className={td}>
                            <div className="t-primary">{e.concept}</div>
                            <div className="text-[11px] t-faint">
                              {e.kind !== 'NORMAL' && `${CTB_ENTRY_KIND_LABEL[e.kind]} · `}
                              {e.reversesId && 'Anula a otro asiento · '}
                              {e.reversedById && 'Anulado por un contra-asiento · '}
                              {(() => {
                                const origin = entryOrigin(e.source)
                                if (!origin) return null
                                return (
                                  <span className="text-toga-300">
                                    Automático ·{' '}
                                    {origin.href ? (
                                      <Link to={origin.href} className="hover:underline">
                                        {origin.label}
                                      </Link>
                                    ) : (
                                      origin.label
                                    )}
                                  </span>
                                )
                              })()}
                            </div>
                          </td>
                          <td className={td}>
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded-full border ${STATUS_CLS[e.status]}`}
                            >
                              {CTB_ENTRY_STATUS_LABEL[e.status]}
                            </span>
                          </td>
                          <td className={`${tdR} font-semibold`}>{formatCents(e.totalCents)}</td>
                          <td className={`${td} whitespace-nowrap text-right`}>
                            {e.status === 'DRAFT' && (
                              <>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() =>
                                    void act(() =>
                                      api.post(`/ctb/entries/${e.id}/post`, { issuerId })
                                    )
                                  }
                                  className="text-xs text-emerald-300 hover:underline mr-2 disabled:opacity-50"
                                >
                                  Contabilizar
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setModal({ entry: e })}
                                  className="p-1 t-muted hover:text-toga-300"
                                  aria-label="Editar"
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() =>
                                    void act(() =>
                                      api.post(`/ctb/entries/${e.id}/discard`, { issuerId })
                                    )
                                  }
                                  className="p-1 t-muted hover:text-rose-300"
                                  aria-label="Descartar"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </>
                            )}
                            {e.status === 'POSTED' &&
                              !e.reversedById &&
                              !e.reversesId &&
                              e.source && (
                                <span
                                  className="text-[11px] t-faint"
                                  title="Se anula desde su documento: rectificar o anular la factura, anular el cobro, eliminar la provisión"
                                >
                                  Desde su documento
                                </span>
                              )}
                            {e.status === 'POSTED' &&
                              !e.reversedById &&
                              !e.reversesId &&
                              !e.source && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setReversing(e)
                                    setReason('')
                                  }}
                                  className="text-xs t-muted hover:text-rose-300 inline-flex items-center gap-1"
                                >
                                  <RotateCcw size={12} /> Anular
                                </button>
                              )}
                          </td>
                        </tr>
                        {expanded && (
                          <tr className="border-b border-base/50 bg-black/10">
                            <td />
                            <td colSpan={6} className="px-3 py-2">
                              <table className="w-full text-xs">
                                <tbody>
                                  {e.lines.map((l) => (
                                    <tr key={l.id}>
                                      <td className="py-0.5 pr-3 font-mono t-secondary whitespace-nowrap">
                                        {l.accountCode}
                                      </td>
                                      <td className="py-0.5 pr-3 t-muted">
                                        {l.accountName}
                                        {l.concept ? ` — ${l.concept}` : ''}
                                      </td>
                                      <td className="py-0.5 text-right font-mono w-28">
                                        {l.debitCents ? formatCents(l.debitCents) : ''}
                                      </td>
                                      <td className="py-0.5 text-right font-mono w-28">
                                        {l.creditCents ? formatCents(l.creditCents) : ''}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
          {truncated && (
            <p className="px-5 py-2 text-xs text-amber-300">
              Hay más asientos de los que caben: acota por estado o texto.
            </p>
          )}
          <Note>
            Los asientos se numeran por ejercicio al contabilizarlos. Los borradores no cuentan en
            el mayor ni en las sumas y saldos.
          </Note>
        </section>
      )}

      {modal && (
        <EntryModal
          issuerId={issuerId}
          accounts={accounts}
          entry={modal.entry}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null)
            reload()
          }}
        />
      )}

      {reversing &&
        createPortal(
          <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4"
            onClick={() => setReversing(null)}
          >
            <form
              onClick={(e) => e.stopPropagation()}
              onSubmit={(e) => {
                e.preventDefault()
                void act(async () => {
                  await api.post(`/ctb/entries/${reversing.id}/reverse`, { issuerId, reason })
                  setReversing(null)
                })
              }}
              className="w-full max-w-md surface-deep p-5 space-y-3"
            >
              <h3 className="text-base font-semibold t-primary">
                Anular el asiento {reversing.number}
              </h3>
              <p className="text-xs t-muted">
                Se crea un contra-asiento enlazado con las líneas al revés. Solo es posible si el
                periodo sigue abierto y sin declarar; si no, hay que rectificar en el periodo
                abierto.
              </p>
              <label className="block">
                <span className="text-xs t-muted">Motivo (obligatorio)</span>
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required
                  maxLength={300}
                  className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
                />
              </label>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setReversing(null)}
                  className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={busy || !reason.trim()}
                  className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-rose-600 to-rose-700 disabled:opacity-50"
                >
                  Anular
                </button>
              </div>
            </form>
          </div>,
          document.body
        )}
    </div>
  )
}
